import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Can, ConfirmDialog, PromptDialog, formatDateTime } from '@/kit/ui'
import { canBuild, canMakeLive, canRollBack, canRunTests, compareVersions, isValidVersion, nextVersion, ofCode, repoLabel } from '@/lib/batch'
import { useCodeStore } from '@/store/codeStore'
import { useFlowStore } from '@/store/flowStore'
import { batchOf } from '@/lib/flow'
import type { Batch, Lender } from '@/types'
import { BatchStatusBadge } from './BatchBits'
import { playgroundUrl } from '../links'
import { useBatchActions } from './useBatchActions'
import { ProductPicker, useBatchProduct } from './ProductPicker'

/**
 * 3.2 API batches (F2-4): the version table with the one-way status and the actions; copied in spirit from the_Kit's
 * CodeBatchScreen. BP-1: one product's batch at a time (the product picker), each with its own versions.
 */
export function BatchesSubTab({ lender }: { lender: Lender }) {
  const navigate = useNavigate()
  const batches = useCodeStore((s) => s.batches)
  const { choices, chosen, productSlug, choose } = useBatchProduct(lender)
  const mine = useMemo(() => ofCode(batches, lender.slug, productSlug).sort((a, b) => compareVersions(b.version, a.version)), [batches, lender.slug, productSlug])
  const codeKey = productSlug ?? lender.slug
  const flows = useFlowStore((s) => s.flows)
  /** the workflow versions that run on each batch (live ones follow the live batch, drafts pin one) */
  const onBatch = (b: Batch) => flows.filter((f) => f.lenderSlug === lender.slug && f.status !== 'retired' && batchOf(f, batches)?.id === b.id).map((f) => `${f.name.split('.').slice(1).join('.')} ${f.version}${f.status === 'live' ? '' : ` (${f.status.replace('_', ' ')})`}`)
  const { can, busy, runAll, build, makeLive, rollBack, newVersion } = useBatchActions()
  const [lines, setLines] = useState<string[]>([])
  const [confirm, setConfirm] = useState<{ kind: 'live' | 'rollback'; batch: Batch } | null>(null)
  const [creatingFrom, setCreatingFrom] = useState<Batch | null | 'none'>(null)

  const open = (b: Batch) => navigate(playgroundUrl(lender.slug, b.version, undefined, b.productSlug ?? null))
  const who = (email: string) => email.split('@')[0]

  return (
    <>
      <ProductPicker choices={choices} chosen={chosen} onChoose={choose} />
      <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
        <p className="max-w-3xl text-xs text-muted">
          One API batch per product, each with its own versions (<code>{codeKey}.batchApi.&lt;version&gt;</code>, git <code>{repoLabel(codeKey)}</code>). Status goes one way: draft → tests passed → built → live. Built or live = read-only; a change = <b>New version from this</b>. A new version of one product never changes another.
        </p>
        <Can permission="code.edit">
          <button type="button" className="btn btn-primary" disabled={!chosen} onClick={() => setCreatingFrom(mine.find((b) => b.status === 'live') ?? 'none')} data-testid="new-batch-version">+ New batch version</button>
        </Can>
      </div>
      <div className="card-flush overflow-x-auto">
        <table className="table">
          <thead>
            <tr><th>Version</th><th>Status</th><th>Made from</th><th>Endpoints</th><th>Tests</th><th>Git tag · commit</th><th>Bundle in storage</th><th>Workflows on it</th><th>Who · when</th><th /></tr>
          </thead>
          <tbody>
            {mine.map((b) => (
              <tr key={b.id} data-testid={`batch-row-${b.version}`}>
                <td className="mono"><b>{b.version}</b></td>
                <td><BatchStatusBadge status={b.status} /></td>
                <td className="mono">{b.madeFrom ?? '—'}</td>
                <td title={b.manifest.endpoints.map((e) => e.path).join(', ')}>{b.manifest.endpoints.length} endpoint{b.manifest.endpoints.length === 1 ? '' : 's'} · {b.files.length} files</td>
                <td className={b.testRun && b.testRun.passed < b.testRun.total ? 'text-red-700 dark:text-red-300' : ''}>{b.testRun ? `${b.testRun.passed} / ${b.testRun.total}` : `— / ${b.tests.length}`}</td>
                <td className="mono whitespace-nowrap">{b.git.tag ?? '—'} · {b.git.commit}</td>
                <td className="mono">{b.bundle ? <>{b.bundle.key}<br /><span className="text-muted">{b.bundle.checksum.slice(0, 19)}…</span></> : b.status === 'retired' ? 'kept' : '—'}</td>
                <td>{onBatch(b).join(' · ') || '—'}</td>
                <td className="whitespace-nowrap">{who(b.madeLiveAt ? b.bundle?.builtBy ?? b.createdBy : b.commits[0]?.who ?? b.createdBy)} · {formatDateTime(b.madeLiveAt ?? b.updatedAt)}</td>
                <td className="text-right">
                  <div className="flex flex-wrap justify-end gap-1">
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => open(b)}>{b.status === 'draft' || b.status === 'tests_passed' ? 'Open' : 'View'}</button>
                    {canRunTests(b) && can('code.test') && (
                      <button type="button" className="btn btn-secondary btn-sm" disabled={busy === b.id} onClick={async () => setLines((await runAll(b, lender)).lines)}>Run all tests</button>
                    )}
                    {(b.status === 'draft' || b.status === 'tests_passed') && can('code.publish') && (
                      <button type="button" className="btn btn-primary btn-sm" disabled={!canBuild(b, can) || busy === b.id} title={canBuild(b, can) ? 'Tag + bundle with checksum; the version is fixed' : 'After Run all tests is green'} onClick={async () => setLines(await build(b))}>Build</button>
                    )}
                    {b.status === 'built' && can('code.publish') && (
                      <button type="button" className="btn btn-accent btn-sm" disabled={!canMakeLive(b, can)} onClick={() => setConfirm({ kind: 'live', batch: b })}>Make live</button>
                    )}
                    {(b.status === 'live' || b.status === 'built') && can('code.edit') && (
                      <button type="button" className="btn btn-secondary btn-sm" onClick={() => setCreatingFrom(b)}>New version from this</button>
                    )}
                    {b.status === 'previous' && can('code.publish') && (
                      <button type="button" className="btn btn-accent btn-sm" disabled={!canRollBack(b, can)} onClick={() => setConfirm({ kind: 'rollback', batch: b })}>Roll back to this</button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {mine.length === 0 && <tr><td colSpan={10} className="text-center text-muted">No batch yet for {chosen?.label ?? 'this product'}. Create API (3.1) or + New batch version makes its first one (1.0.0).</td></tr>}
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-2xs text-muted">
        <b>C1:</b> when a newer batch goes live, live workflows move to it by themselves after a check that every file and outcome they use still exists; roll-back moves them back. Draft workflows stay where they are. Admins build and make live; editors edit and test.
      </p>
      {lines.length > 0 && (
        <div className="card mt-3 !p-0">
          <div className="flex items-center justify-between border-b border-slate-200 px-3 py-1.5 text-2xs dark:border-slate-700"><b>Last action</b><button type="button" className="btn btn-ghost btn-sm" onClick={() => setLines([])}>Clear</button></div>
          <pre className="code-block max-h-64 overflow-auto rounded-t-none" data-testid="batch-console">{lines.join('\n')}</pre>
        </div>
      )}
      <ConfirmDialog
        isOpen={confirm !== null}
        onClose={() => setConfirm(null)}
        type={confirm?.kind === 'rollback' ? 'warning' : 'info'}
        title={confirm?.kind === 'rollback' ? `Roll back to ${confirm.batch.version}?` : `Make ${confirm?.batch.version} live?`}
        message={
          confirm?.kind === 'rollback'
            ? `${confirm.batch.version} becomes live again from its kept bundle; the current live version becomes previous; live workflows move back. One click, no rebuild.`
            : `The Gateway runner pulls the bundle from storage (checksum checked). Live workflows on the current live batch move to ${confirm?.batch.version ?? ''} after the C1 check; the current live version becomes previous (kept for roll-back).`
        }
        confirmText={confirm?.kind === 'rollback' ? 'Roll back' : 'Make live'}
        onConfirm={async () => {
          if (!confirm) return
          const c = confirm
          setConfirm(null)
          setLines(await (c.kind === 'rollback' ? rollBack(c.batch) : makeLive(c.batch)))
        }}
      />
      <PromptDialog
        isOpen={creatingFrom !== null}
        onClose={() => setCreatingFrom(null)}
        title={`New batch version · ${chosen?.label ?? ''}`}
        message={creatingFrom && creatingFrom !== 'none' ? `A copy of ${creatingFrom.version} (${creatingFrom.files.length} files, ${creatingFrom.tests.length} test events) as a draft on its own git branch.` : `An empty first batch (README only) of ${chosen?.label ?? 'this product'} as a draft.`}
        inputLabel="Version number"
        initialValue={nextVersion(mine)}
        validate={(v) => (isValidVersion(v) ? (mine.some((b) => b.version === v) ? `${v} already exists` : null) : 'Must look like 1.2.0')}
        confirmText="Create"
        onConfirm={(v) => {
          const b = newVersion(lender.slug, creatingFrom && creatingFrom !== 'none' ? creatingFrom.id : null, v, productSlug)
          setCreatingFrom(null)
          if (b) open(b)
        }}
      />
    </>
  )
}
